import React, { useState, useEffect } from 'react';
import { useAuth } from '../auth/AuthContext';
import '../../styles/admin-dashboard.css';
import { IcoClipboard } from '../dashboard/DashIcons';

const categories = ['TOTAL', 'REG', 'OT1', 'OT2', 'HOL', 'SIC', 'PTO', 'LUN', 'MIL', 'OTHER PAY'];

const MultiDayEntryModal = ({ onClose, refetch, employees }) => {
  const { authFetch, user } = useAuth();
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [entries, setEntries] = useState({});
  const [payPeriod, setPayPeriod] = useState({ start: null, end: null, label: '' });
  const [dates, setDates] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const today = new Date();
    const currentDay = today.getDate();

    let startDate, endDate;

    if (currentDay >= 8 && currentDay <= 22) {
      startDate = new Date(today.getFullYear(), today.getMonth(), 8);
      endDate = new Date(today.getFullYear(), today.getMonth(), 22);
    } else if (currentDay >= 23) {
      startDate = new Date(today.getFullYear(), today.getMonth(), 23);
      endDate = new Date(today.getFullYear(), today.getMonth() + 1, 7);
    } else {
      startDate = new Date(today.getFullYear(), today.getMonth() - 1, 23);
      endDate = new Date(today.getFullYear(), today.getMonth(), 7);
    }

    const label = `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    setPayPeriod({ start: startDate, end: endDate, label });

    const periodDates = [];
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      periodDates.push(currentDate.toISOString().split('T')[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }
    setDates(periodDates);
  }, []);

  const handleChange = (date, category, value) => {
    setEntries(prev => ({
      ...prev,
      [date]: { ...prev[date], [category]: parseFloat(value) || 0 },
    }));
  };

  const handleSubmit = async () => {
    if (!selectedEmployee) {
      alert('Please select an employee');
      return;
    }

    // Build shifts grouped by week for the schedule API
    // Each date becomes a shift on the appropriate weekday
    const shiftsToSave = [];

    Object.keys(entries).forEach(date => {
      const dailyEntry = entries[date];
      const totalHours = ['REG', 'OT1', 'OT2', 'HOL'].reduce((sum, cat) => {
        return sum + (parseFloat(dailyEntry[cat]) || 0);
      }, 0);

      if (totalHours <= 0) return;

      const dayDate = new Date(date + 'T12:00:00');
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const dayName = dayNames[dayDate.getDay()];

      shiftsToSave.push({
        date,
        day: dayName,
        employeeName: selectedEmployee,
        hours: totalHours,
        reg: dailyEntry['REG'] || 0,
        ot1: dailyEntry['OT1'] || 0,
        ot2: dailyEntry['OT2'] || 0,
        hol: dailyEntry['HOL'] || 0,
        sic: dailyEntry['SIC'] || 0,
        pto: dailyEntry['PTO'] || 0,
        otherPay: dailyEntry['OTHER PAY'] || 0,
        lunch: dailyEntry['LUN'] || 0,
        mileage: dailyEntry['MIL'] || 0,
        notes: '',
        status: 'pending', // Awaiting manager review
      });
    });

    if (shiftsToSave.length === 0) {
      alert('No hours entered to save.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await authFetch('/api/schedules/pending-entries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employeeName: selectedEmployee,
          shifts: shiftsToSave,
          submittedBy: user?.id,
        }),
      });

      const data = await res.json();

      if (res.ok) {
        alert(`✅ ${shiftsToSave.length} entries submitted for manager review!`);
        if (refetch) refetch();
        onClose();
      } else {
        alert(data.message || '❌ Failed to submit entries');
      }
    } catch (err) {
      console.error('❌ Network error:', err);
      alert('Error submitting entries. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (dates.length === 0) {
    return (
      <div className="modal-backdrop">
        <div className="modal-content"><p>Loading pay period...</p></div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop">
      <div className="modal-content" style={{ maxWidth: '95vw', width: 'auto' }}>
        <h2>Multi-Day Entry</h2>
        <p>
          Enter hours for the current pay period: <strong>{payPeriod.label}</strong>
        </p>
        <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: '-0.5rem 0 1rem' }}>
          Entries will be submitted to your manager for review before going to payroll.
        </p>

        <select
          value={selectedEmployee}
          onChange={(e) => setSelectedEmployee(e.target.value)}
          style={{ marginBottom: '1rem', padding: '0.5rem', fontSize: '1rem' }}
        >
          <option value="">Select Employee</option>
          {employees.map(name => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>

        <div className="table-scroll-wrapper" style={{ overflowX: 'auto', maxHeight: '60vh' }}>
          <table className="multi-day-table">
            <thead>
              <tr>
                <th style={{ position: 'sticky', left: 0, background: 'white', zIndex: 2 }}>Category</th>
                {dates.map(date => (
                  <th key={date}>
                    {new Date(date + 'T12:00:00').toLocaleDateString('en-US', {
                      month: 'numeric', day: 'numeric', weekday: 'short'
                    })}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {categories.map(category => (
                <tr key={category}>
                  <td style={{ position: 'sticky', left: 0, background: 'white', fontWeight: 'bold' }}>
                    {category}
                  </td>
                  {dates.map(date => {
                    if (category === 'TOTAL') {
                      const total = ['REG', 'OT1', 'OT2', 'HOL'].reduce((sum, cat) => {
                        return sum + (parseFloat(entries[date]?.[cat]) || 0);
                      }, 0);
                      return (
                        <td key={`${category}-${date}`}>
                          <input
                            type="number"
                            value={total || ''}
                            readOnly
                            style={{ width: '4rem', backgroundColor: '#f4f4f4', fontWeight: 'bold', border: '1px solid #ccc' }}
                          />
                        </td>
                      );
                    }
                    return (
                      <td key={`${category}-${date}`}>
                        <input
                          type="number"
                          step="0.01"
                          value={entries[date]?.[category] || ''}
                          onChange={(e) => handleChange(date, category, e.target.value)}
                          style={{ width: '4rem' }}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: '1rem', display: 'flex', gap: '1rem' }}>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'linear-gradient(135deg, #22c55e 0%, #0f4d2e 100%)',
              color: 'white', border: 'none', borderRadius: '8px',
              cursor: submitting ? 'not-allowed' : 'pointer',
              fontWeight: '600', opacity: submitting ? 0.7 : 1
            }}
          >
            {submitting ? 'Submitting...' : <><IcoClipboard size={14}/> Submit for Manager Review</>}
          </button>
          <button
            onClick={onClose}
            style={{ padding: '0.75rem 1.5rem', background: '#4b5563', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default MultiDayEntryModal;